import { SharedLinkState, SharedLinkCreate, SharedLinkListItem } from '@/store/types/link'

import { apiCreateSharedLink, apiDeleteSharedLink, apiListUserLinks } from '@/api/shareLinks'

import { GetterTree, ActionTree, MutationTree, Module } from 'vuex'
import { RootState } from '@/store/types/root'

import { isAlreadyPending, initRequestHelper } from '../root'
import { keys2Camel } from '@/services/objects'
import { getSharedLinkURL } from '@/services/url'

/**
 * This is a module for managing links
 *
 * many of its functionalities have been or are being copied from the files module,
 * where the two domains have been cramped in until now.
 * @returns
 */

const getDefaultState = (): SharedLinkState => {
  return {
    shared: [],
  }
}

const moduleState = getDefaultState()

const moduleGetters: GetterTree<SharedLinkState, RootState> = {
  shareLinkRequest(_state, _getters, rootState) {
    return rootState.requests.linkShare
  },
  unshareLinkRequest(_, _getters, rootState) {
    return rootState.requests.linkUnshare
  },
  listLinksRequest(_state, _getters, rootState) {
    return rootState.requests.linkList
  },
  shared(state) {
    return state.shared
  },
}

const moduleActions: ActionTree<SharedLinkState, RootState> = {
  async clearBrowser({ commit }) {
    commit('clearDriveSelection')
    commit('clearPath')
    commit('clearData')
  },

  async shareLink({ commit, getters }, sharedLinkCreate: SharedLinkCreate) {
    if (isAlreadyPending(getters.shareLinkRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'linkShare')
    try {
      const apiResponse = await apiCreateSharedLink(sharedLinkCreate)
      const link = getSharedLinkURL(apiResponse.hash)
      setSuccess()
      return link
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  async unshareLink({ commit, getters }, hash: string) {
    if (isAlreadyPending(getters.unshareLinkRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'linkUnshare')
    try {
      await apiDeleteSharedLink(hash)
      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  async listLinks({ commit }) {
    const [setSuccess, setFailure] = initRequestHelper(commit, 'linkList')
    try {
      const rawLinkList = await apiListUserLinks()
      const translatedLinkList = keys2Camel(rawLinkList)

      commit('SET_SHARED_LINKS', translatedLinkList)
      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },
  async resetSharedLinksState({ commit }) {
    commit('resetState')
  },
}

const moduleMutations: MutationTree<SharedLinkState> = {
  SET_SHARED_LINKS(state, links: SharedLinkListItem[]) {
    state.shared = links
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  },
}

const link: Module<SharedLinkState, RootState> = {
  namespaced: true,
  // prefixed with "module-", so that they do not get shadowed by desctructured context
  state: moduleState,
  getters: moduleGetters,
  actions: moduleActions,
  mutations: moduleMutations,
}
export default link
